/* Customer account API DTOs (frontend mirror of `/customer-account/*`).
 *
 * Plan §"Backend/API Plan" + §"Pagination contract":
 *   - default limit = 20, max = 100.
 *   - `cursor` is opaque, server-generated, pass-through only.
 */

import type { LogoChipBackgroundSettings, LogoChipRimSettings } from 'pi-kiosk-shared';

export interface CustomerMeDTO {
  customerId: number;
  tenantId?: number;
  /**
   * Home tenant code for ACTIVE sessions; omitted on identity-only hub `/me`.
   */
  tenantCode?: string | null;
  /** Present on tenant ACTIVE `/me`; omitted on identity-only hub `/me`. */
  membershipStatus?: 'ACTIVE';
  displayName?: string | null;
  email?: string | null;
  phone?: string | null;
  username?: string | null;
  /** ISO timestamp when email was verified; null/omitted = unverified. */
  emailVerifiedAt?: string | null;
  /** ISO timestamp when phone was verified; null/omitted = unverified. */
  phoneVerifiedAt?: string | null;
  /**
   * Wave 3 — native `both_enforce` with a still-required unverified provided channel.
   * Shell/pipeline read this from `/me` (CustomerSession stays `{ customerId }` only).
   */
  mustCompleteVerification?: boolean;
  /** False when the account has no local password (OIDC-only / set-credentials CTA). */
  hasPassword?: boolean;
  /** Active TERMS + PRIVACY grants at policy v1 for this tenant. */
  hasLegalGrant?: boolean;
  /**
   * Identity-only hub `/me` only — always `[]` until the customer joins a tenant.
   * Omitted on tenant ACTIVE `/me` (use `/customer-account/memberships`).
   */
  memberships?: [];
}

export interface CustomerMembershipItemDTO extends LogoChipRimSettings, LogoChipBackgroundSettings {
  tenantId: number;
  tenantCode: string;
  tenantName: string | null;
  membershipStatus: 'ACTIVE' | 'PROSPECT' | 'SUSPENDED' | 'REVOKED';
  status?: 'ACTIVE' | 'PROSPECT' | 'SUSPENDED' | 'REVOKED';
  isCurrentTenant: boolean;
  /** Signed tenant square logo stream URL, or null when absent / applyToCustomerPwa off. Live FE ignores wire wordmarkUrl. */
  logoUrl: string | null;
  /** Optional dark-regime square logo; falls back to {@link logoUrl}. */
  logoUrlDark: string | null;
}

export interface CustomerMembershipsDTO {
  walletVisible?: boolean;
  loyalty?: {
    enabled: boolean;
    walletVisible: boolean;
  };
  promotions?: {
    enabled: boolean;
  };
  /**
   * Current-tenant payment claim mode from Account Settings.
   * Absent on older *successful* API responses — treat as enabled (ADMIN_APPROVAL default).
   * Do not treat missing DTO / load / error as enabled — use resolvePaymentClaimsAffordance.
   */
  paymentClaims?: {
    enabled: boolean;
    mode: 'DISABLED' | 'ADMIN_APPROVAL' | 'AUTO';
  };
  memberships: CustomerMembershipItemDTO[];
}

export interface CustomerRefreshSessionDTO {
  id: string;
  deviceLabel: string | null;
  userAgentHash: string | null;
  ipHash: string | null;
  createdAt: string;
  lastSeenAt: string;
  isCurrent: boolean;
}

export interface CustomerRefreshSessionsDTO {
  sessions: CustomerRefreshSessionDTO[];
}

export interface RevokeCustomerRefreshSessionDTO {
  revoked: true;
  isCurrent: boolean;
}

export interface CursorPage<T> {
  items: T[];
  nextCursor: string | null;
}

export type ReceiptAvailability = 'INTERNAL_PDF' | 'EXTERNAL_PROVIDER' | 'NONE';
export type ReceiptStatus = 'none' | 'pending' | 'ready' | 'failed';

export interface DonationContributionDTO {
  contributionId: string;
  transactionId: number;
  donatedAt: string;
  projectCode: string;
  projectName: string;
  amountMinor: number;
  currency: string;
  paymentId: string | null;
  note?: string | null;
  receiptStatus: ReceiptStatus;
  receiptKind?: 'PRODUCT_RECEIPT' | 'DONATION_CONFIRMATION';
  receiptAvailability: ReceiptAvailability;
  downloadPath: string | null;
  issuedAt: string;
  documentNumber: string | null;
}

export interface YearlyDonationTotal {
  year: number;
  amountMinor: number;
  currency: string;
  contributionCount: number;
}

export interface ProjectDonationTotal {
  projectCode: string;
  projectName: string;
  amountMinor: number;
  currency: string;
  contributionCount: number;
}

export interface DonationsSummaryDTO {
  yearlyTotals: YearlyDonationTotal[];
  projectTotals: ProjectDonationTotal[];
}

export type ProductCollectionMode = 'PAY_AT_KIOSK' | 'PREPAY_COLLECT_LATER';

export interface PaymentHistoryItemDTO {
  transactionId: number;
  paymentId: string;
  /** Owning organization — history spans all tenants for the logged-in customer. */
  tenantId: number;
  /**
   * Owning shop slug — BE G7 contract: non-empty on every owned payment row
   * (platform hub + tenant mount). FE still degrades if empty/reserved.
   */
  tenantCode: string;
  tenantName: string;
  purposeType: 'PRODUCT' | 'DONATION';
  status: 'PENDING' | 'INITIATED' | 'COMPLETED' | 'FAILED' | 'CANCELED';
  amountMinor: number;
  currency: string;
  /**
   * BE Buy Again CTA gate for payment-history PRODUCT rows (journey preconditions).
   * When false, FE must hide buy-again even if status is COMPLETED (G1).
   * Optional for back-compat with older API payloads.
   */
  buyAgainEligible?: boolean;
  /** Enterprise QR policy — when false, customer must not show bank-transfer QR. */
  qrVisible?: boolean;
  productCollectionMode?: ProductCollectionMode | null;
  createdAt: string;
  completedAt: string | null;
  receiptStatus: ReceiptStatus;
  receiptKind?: 'PRODUCT_RECEIPT' | 'DONATION_CONFIRMATION';
  receiptAvailability: ReceiptAvailability;
  downloadPath: string | null;
  issuedAt: string;
  documentNumber: string | null;
}

export interface ReceiptLocalePreferenceDTO {
  /** Optional for back-compat; BE should send `default` | `custom`. */
  mode?: 'default' | 'custom';
  locales: string[];
  uiLocale?: string | null;
  /** True iff Default / follow-UI mode (not a locale snapshot). */
  isPlatformDefault: boolean;
}

/** PUT `/customer-account/preferences/receipt-locales` body. */
export type ReceiptLocalePreferencePutBody =
  | { mode: 'default'; uiLocale: string }
  | { mode: 'custom'; locales: string[] };

/** GET `/customer-account/preferences/email-locale` response. */
export interface EmailLocalePreferenceDTO {
  /** Optional for back-compat; BE should send `default` | `custom`. */
  mode?: 'default' | 'custom';
  /** Effective / custom email locale (display); null when unknown. */
  locale: string | null;
  uiLocale?: string | null;
  /** True iff Default / follow-UI mode. */
  isPlatformDefault: boolean;
}

/** PUT `/customer-account/preferences/email-locale` body. */
export type EmailLocalePreferencePutBody =
  | { mode: 'default'; uiLocale: string }
  | { mode: 'custom'; locale: string };

export const DEFAULT_PAGE_LIMIT = 20;
export const MAX_PAGE_LIMIT = 100;
